import { set } from "date-fns";
import { UseFormReturn } from "react-hook-form";

import { RoundInput } from "@/components/forms/validation/round";
import { PgnProviderType } from "@/gen/graphql";

export const PGN_PROVIDER_DISPLAY_NAMES: Record<string, string> = {
    MANUAL: "Manual",
    POLL_PGN: "Poll PGN",
    STREAMING_PGN: "Streaming PGN",
    LIVECHESS_CLOUD_ROUND: "Livechess Cloud Round",
};

export function getPgnProviderDisplayName(providerType: string): string {
    return PGN_PROVIDER_DISPLAY_NAMES[providerType] || providerType;
}

// Helper function to create default poll times
const getDefaultPollTimes = () => ({
    pollStart: set(new Date(), {
        hours: 12,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
    }),
    pollEnd: set(new Date(), {
        hours: 13,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
    }),
});

// Helper function to create base config with preserved sources
export const createBaseConfig = (
    currentConfig: RoundInput["roundSyncerConfig"],
    pgnProviderType: PgnProviderType
) => ({
    pgnProviderType,
    pollPgnSource: currentConfig?.pollPgnSource,
    streamingPgnSource: currentConfig?.streamingPgnSource,
    livechesscloudRoundId: currentConfig?.livechesscloudRoundId,
    livechesscloudRoundNumber: currentConfig?.livechesscloudRoundNumber,
    pgnDelaySecs: currentConfig?.pgnDelaySecs ?? 0,
});

// Helper function to handle provider type changes
export const handleProviderTypeChange = (form: UseFormReturn<RoundInput>, value: string) => {
    const currentConfig = form.getValues("roundSyncerConfig");
    const defaultTimes = getDefaultPollTimes();

    // Common attributes for all provider types that support polling
    const commonPollAttributes = {
        pollStart: currentConfig?.pollStart || defaultTimes.pollStart,
        pollEnd: currentConfig?.pollEnd || defaultTimes.pollEnd,
        pollCreateGames: currentConfig?.pollCreateGames ?? true,
        pgnDelaySecs: currentConfig?.pgnDelaySecs ?? 0,
    };

    const configMap: Record<string, RoundInput["roundSyncerConfig"]> = {
        MANUAL: createBaseConfig(currentConfig, "MANUAL"),
        POLL_PGN: {
            ...createBaseConfig(currentConfig, "POLL_PGN"),
            pollPgnSource: currentConfig?.pollPgnSource || "",
            ...commonPollAttributes,
        },
        STREAMING_PGN: {
            ...createBaseConfig(currentConfig, "STREAMING_PGN"),
            streamingPgnSource: currentConfig?.streamingPgnSource || "",
            ...commonPollAttributes,
        },
        LIVECHESS_CLOUD_ROUND: {
            ...createBaseConfig(currentConfig, "LIVECHESS_CLOUD_ROUND"),
            livechesscloudRoundId: currentConfig?.livechesscloudRoundId || "",
            livechesscloudRoundNumber: currentConfig?.livechesscloudRoundNumber || 1,
            ...commonPollAttributes,
        },
    };

    const newConfig = configMap[value];
    if (newConfig) {
        form.setValue("roundSyncerConfig", newConfig, {
            shouldDirty: true,
            shouldTouch: true,
        });
    }
};
